import {MvsCoreService} from "../../../../core/shared/service/mvs-core.service";
import {MvsCrudService} from "../../../../core/shared/service/crud/mvs-crud.service";
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {CbDomainConceptDto} from "../../model/dto/entity/cb-domain-concept.dto";
import {MvsMessageService} from "../../../../core/shared/service/message/mvs-message.service";

/**
 * CB Concept Editor Component.
 * Form for creating/editing domain concepts.
 */
@Component({
    selector: 'cb-concept-editor',
    templateUrl: './cb-concept-editor.component.html',
    styleUrls: ['./cb-concept-editor.component.scss'],
    standalone: false
})
export class CbConceptEditorComponent implements OnInit, OnChanges {

    conceptService: MvsCrudService;
    @Input() concept: CbDomainConceptDto | null = null;
    @Input() isNew: boolean = true;

    @Output() saved = new EventEmitter<CbDomainConceptDto>();
    @Output() cancelled = new EventEmitter<void>();

    form: FormGroup;
    saving: boolean = false;

    // Predefined categories for autocomplete
    categoryOptions: string[] = [
        'Customer Management',
        'Contract Management',
        'Billing',
        'Agent Management',
        'Document Management',
        'Workflow',
        'Notification',
        'General'
    ];

    filteredCategories: string[] = [];

    constructor(
        protected coreService: MvsCoreService,
        
        private fb: FormBuilder,
        private messageService: MvsMessageService
    ) {
        this.conceptService = this.coreService.getCrudService("cb.CbConcept");

        this.initForm();
    }

    ngOnInit(): void {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['concept']) {
            if (this.concept) {
                this.populateForm(this.concept);
            } else {
                this.resetForm();
            }
        }
    }

    /**
     * Initialize the form.
     */
    private initForm(): void {
        this.form = this.fb.group({
            term: ['', [Validators.required, Validators.minLength(2)]],
            definition: ['', [Validators.required, Validators.minLength(10)]],
            category: ['', Validators.required]
        });
    }

    /**
     * Populate form with existing concept.
     */
    private populateForm(concept: CbDomainConceptDto): void {
        this.form.patchValue({
            term: concept.term || '',
            definition: concept.definition || '',
            category: concept.category || ''
        });
    }

    /**
     * Reset form to empty state.
     */
    private resetForm(): void {
        this.form.reset({
            term: '',
            definition: '',
            category: ''
        });
    }

    /**
     * Filter categories for autocomplete.
     */
    filterCategories(event: { query: string }): void {
        const query = event.query.toLowerCase();
        this.filteredCategories = this.categoryOptions.filter(c =>
            c.toLowerCase().includes(query)
        );

        // Add the query itself if not in the list (allow custom categories)
        if (!this.filteredCategories.includes(event.query) && event.query.trim()) {
            this.filteredCategories.push(event.query);
        }
    }

    /**
     * Save the concept.
     */
    save(): void {
        if (!this.form.valid) {
            this.messageService.showErrorMessage('Error', 'Please fill in all required fields correctly');
            return;
        }

        this.saving = true;
        const formValue = this.form.value;

        if (this.isNew) {
            // Create new concept
            const newConcept: Partial<CbDomainConceptDto> = {
                name: formValue.term,
                ...formValue
            };

            this.conceptService.create(newConcept as CbDomainConceptDto).subscribe({
                next: (created) => {
                    this.saving = false;
                    this.messageService.showSuccessMessage('Success', 'Concept created successfully');
                    this.saved.emit(created as CbDomainConceptDto);
                },
                error: (error) => {
                    console.error('Error creating concept:', error);
                    this.messageService.showErrorMessage('Error', 'Failed to create concept');
                    this.saving = false;
                }
            });
        } else {
            // Update existing concept
            const updatedConcept = {
                ...this.concept,
                ...formValue,
                name: formValue.term
            };

            this.conceptService.update(updatedConcept).subscribe({
                next: (updated) => {
                    this.saving = false;
                    this.messageService.showSuccessMessage('Success', 'Concept updated successfully');
                    this.saved.emit(updated as CbDomainConceptDto);
                },
                error: (error) => {
                    console.error('Error updating concept:', error);
                    this.messageService.showErrorMessage('Error', 'Failed to update concept');
                    this.saving = false;
                }
            });
        }
    }

    /**
     * Cancel editing.
     */
    cancel(): void {
        this.cancelled.emit();
    }

    /**
     * Get form control error state.
     */
    hasError(controlName: string): boolean {
        const control = this.form.get(controlName);
        return control ? control.invalid && control.touched : false;
    }

    /**
     * Get error message for a control.
     */
    getErrorMessage(controlName: string): string {
        const control = this.form.get(controlName);
        if (!control) return '';

        if (control.errors?.['required']) {
            return 'This field is required';
        }
        if (control.errors?.['minlength']) {
            const min = control.errors['minlength'].requiredLength;
            return `Minimum ${min} characters required`;
        }
        return '';
    }
}
